// §10 — content transfer: "one service, three paths, one permission model".
//
// This module is the permission model. Clipboard (§10.1), drag between windows (§10.2) and Share
// (§10.3) all end the same way — the shell hands a payload to a window — and §10 says that hand-off
// always mints a **payload-scoped capability token**:
//
//   • it authorizes *that content*, for *that transaction*, and nothing else;
//   • it expires on the receiving window's next navigation, or after five minutes, whichever first;
//   • **it never confers filesystem access.**
//
// The last one is a property of what this module *cannot do*, not of what it declines to do: it
// takes no filesystem dependency at all, so there is no path by which a redeemed token could
// become a read. An item's `path` is a name the receiver may or may not already be allowed to
// open; the token says nothing about it, and §9.5 still decides.
import type { Payload } from '@hangar/shared';
import { createTokenMinter, isTokenShaped, type RandomBytes } from '../tokens';

/** §10 — five minutes, or the receiving window's next navigation. */
export const PAYLOAD_TOKEN_TTL_MS = 5 * 60 * 1000;

export interface PayloadGrant {
  token: string;
  /** The window instance this payload was handed to. No other window can redeem it. */
  instanceKey: string;
  issuedAt: number;
  /** Which of that window's lifetimes it belongs to. A navigation ends the one it was issued in. */
  generation: number;
}

export interface TransferTokens {
  /**
   * Mint a token for one delivery of `payload` to `instanceKey`.
   *
   * The payload is held here, not sent with the token: §10.2's drop and §10.3's Share both hand
   * the receiver a token first and let it fetch the content, so a payload that is never redeemed
   * never crosses into the frame at all.
   */
  mint(payload: Payload, instanceKey: string, at: number): string;
  /**
   * The payload this token authorizes, or `null`.
   *
   * Redemption is **single-use**: a delivery is a transaction, and a token that could be replayed
   * would be a standing grant to content the user handed over once.
   */
  redeem(token: string, instanceKey: string, at: number): Payload | null;
  /** Peek without spending, for the Inspector and the tests. */
  grantFor(token: string): PayloadGrant | undefined;
  /**
   * §10 — the receiving window navigated. Every token issued to it before now is dead, even one
   * minted a second ago: the document that was going to redeem it no longer exists.
   */
  navigated(instanceKey: string): void;
  /** The window closed. Nothing it held survives it. */
  revoke(instanceKey: string): void;
  /** Drop everything past its five minutes. Returns how many went. */
  sweep(at: number): number;
  live(): number;
}

interface Held extends PayloadGrant {
  payload: Payload;
}

export interface TransferTokenOptions {
  randomBytes?: RandomBytes;
  ttlMs?: number;
}

export function createTransferTokens(options: TransferTokenOptions = {}): TransferTokens {
  const ttlMs = options.ttlMs ?? PAYLOAD_TOKEN_TTL_MS;
  const minter = createTokenMinter(options.randomBytes);
  const held = new Map<string, Held>();
  /** How many lifetimes each window has had. Bumped by a navigation. */
  const generation = new Map<string, number>();

  function forget(token: string): void {
    held.delete(token);
    minter.retire(token);
  }

  function expired(grant: Held, at: number): boolean {
    if (at - grant.issuedAt >= ttlMs) return true;
    return (generation.get(grant.instanceKey) ?? 0) !== grant.generation;
  }

  return {
    mint(payload, instanceKey, at) {
      // Swept here rather than on a timer: `redeem` already refuses anything past its five
      // minutes, so the only cost of a stale grant is the bytes it holds — and minting is the
      // one moment the shell is definitely about to hold more.
      this.sweep(at);
      const token = minter.mint();
      held.set(token, {
        token,
        instanceKey,
        issuedAt: at,
        generation: generation.get(instanceKey) ?? 0,
        // A copy, so the caller cannot edit what the receiver will get after minting.
        payload: { items: payload.items.map((item) => ({ ...item })) },
      });
      return token;
    },

    redeem(token, instanceKey, at) {
      if (!isTokenShaped(token)) return null;
      const grant = held.get(token);
      if (!grant) return null;
      // Right content, wrong window: a token is scoped to the window it was handed to, so one
      // app cannot redeem what the shell gave another (§9.3's per-instance rule, applied to §10).
      if (grant.instanceKey !== instanceKey) return null;
      if (expired(grant, at)) {
        forget(token);
        return null;
      }
      forget(token);
      return grant.payload;
    },

    grantFor(token) {
      const grant = held.get(token);
      if (!grant) return undefined;
      const { payload: _payload, ...rest } = grant;
      return rest;
    },

    navigated(instanceKey) {
      generation.set(instanceKey, (generation.get(instanceKey) ?? 0) + 1);
      for (const [token, grant] of held) {
        if (grant.instanceKey === instanceKey) forget(token);
      }
    },

    revoke(instanceKey) {
      generation.delete(instanceKey);
      for (const [token, grant] of held) {
        if (grant.instanceKey === instanceKey) forget(token);
      }
    },

    sweep(at) {
      let dropped = 0;
      for (const [token, grant] of held) {
        if (expired(grant, at)) {
          forget(token);
          dropped += 1;
        }
      }
      return dropped;
    },

    live: () => held.size,
  };
}
